/**
 * Network-Specific Phone Number Validation
 * 
 * Validates Ghanaian phone numbers against specific network prefixes
 */

export interface NetworkPrefixes {
  MTN: string[];
  AirtelTigo: string[];
  Telecel: string[];
}

export const NETWORK_PREFIXES: NetworkPrefixes = {
  MTN: ['024', '025', '054', '053', '055', '059'],
  AirtelTigo: ['026', '056', '057', '027'],
  Telecel: ['020', '050']
};

export interface PhoneValidationResult {
  isValid: boolean;
  network: string | null;
  error: string | null;
  warning: string | null; // Soft warning - doesn't block purchase
  formattedNumber: string | null;
}

export const sanitizePhoneInput = (input: string): string => {
  return String(input || '').trim().replace(/\s+/g, '');
};

/**
 * Normalize any Ghana phone format to local 10-digit format (0XXXXXXXXX).
 * Handles all common mistakes:
 *   +233XXXXXXXXX  → 0XXXXXXXXX  (12 digits after stripping +)
 *   233XXXXXXXXX   → 0XXXXXXXXX  (12 digits)
 *   +2330XXXXXXXXX → 0XXXXXXXXX  (13 digits, user kept the 0)
 *   2330XXXXXXXXX  → 0XXXXXXXXX  (13 digits, user kept the 0)
 *   0XXXXXXXXX     → 0XXXXXXXXX  (already correct)
 *   XXXXXXXXX      → 0XXXXXXXXX  (9-digit bare number)
 *   spaces/dashes stripped automatically
 */
export const toGhanaLocalPhone = (input: string): string | null => {
  const raw = String(input || '').trim();
  if (!raw) return null;

  const digitsOnly = raw.replace(/\D/g, '');
  if (!digitsOnly) return null;

  // Already 10-digit local format: 0XXXXXXXXX
  if (digitsOnly.length === 10 && digitsOnly.startsWith('0')) {
    return digitsOnly;
  }

  // 233XXXXXXXXX (12 digits) — standard international without leading 0
  if (digitsOnly.length === 12 && digitsOnly.startsWith('233')) {
    return `0${digitsOnly.slice(3)}`;
  }

  // 2330XXXXXXXXX (13 digits) — user typed 233 then kept the 0: e.g. 2330534788219
  if (digitsOnly.length === 13 && digitsOnly.startsWith('2330')) {
    return `0${digitsOnly.slice(4)}`;
  }

  // 9-digit bare number (no leading 0): e.g. 534788219
  if (digitsOnly.length === 9 && !digitsOnly.startsWith('0')) {
    return `0${digitsOnly}`;
  }

  return null;
};

export const validateGhanaLocalPhone = (input: string): { isValid: boolean; normalized: string | null; error: string | null } => {
  const raw = String(input || '').trim();
  if (!raw) {
    return { isValid: false, normalized: null, error: 'Phone number is required (e.g. 0534788219)' };
  }

  // Try to normalize regardless of format (+233, 233, spaces, dashes)
  const normalized = toGhanaLocalPhone(raw);

  if (!normalized) {
    return {
      isValid: false,
      normalized: null,
      error: 'Enter a valid Ghana phone number (e.g. 0534788219 or +233534788219)'
    };
  }

  if (!/^0[0-9]{9}$/.test(normalized)) {
    return {
      isValid: false,
      normalized: null,
      error: 'Phone number must be 10 digits starting with 0 (e.g. 0534788219)'
    };
  }

  return { isValid: true, normalized, error: null };
};

export const normalizeGhanaPhone = (input: string): string | null => {
  const result = validateGhanaLocalPhone(input);
  return result.isValid ? result.normalized : null;
};

/**
 * Validate phone number format (Ghanaian format: 0XXXXXXXXX)
 */
export const isValidPhoneFormat = (phone: string): boolean => {
  const phoneRegex = /^0[0-9]{9}$/;
  return phoneRegex.test(phone);
};

/**
 * Get network from phone number prefix
 */
export const getNetworkFromPhone = (phone: string): string | null => {
  if (!phone || phone.length < 3) return null;
  
  const prefix = phone.substring(0, 3);
  
  if (NETWORK_PREFIXES.MTN.includes(prefix)) return 'MTN';
  if (NETWORK_PREFIXES.AirtelTigo.includes(prefix)) return 'AirtelTigo';
  if (NETWORK_PREFIXES.Telecel.includes(prefix)) return 'Telecel';
  
  return null;
};

/**
 * Validate phone number against selected network
 * SOFT WARNING ONLY - Never blocks purchase
 */
export const validatePhoneForNetwork = (
  phone: string, 
  selectedNetwork: string
): PhoneValidationResult => {
  const validation = validateGhanaLocalPhone(phone);
  if (!validation.isValid || !validation.normalized) {
    return {
      isValid: false,
      network: null,
      error: validation.error || 'Enter a valid Ghana phone number (e.g. 0534788219)',
      warning: null,
      formattedNumber: null
    };
  }

  const cleanedPhone = validation.normalized;
  
  // Get network from phone prefix
  const detectedNetwork = getNetworkFromPhone(cleanedPhone);
  
  if (!detectedNetwork) {
    return {
      isValid: true, // Allow purchase even if prefix not recognized
      network: null,
      error: null,
      warning: 'Phone number prefix not recognized. Please verify the number is correct.',
      formattedNumber: cleanedPhone
    };
  }
  
  // Normalize network names for comparison
  const normalizedSelected = selectedNetwork.toLowerCase().replace(/\s+/g, '');
  const normalizedDetected = detectedNetwork.toLowerCase().replace(/\s+/g, '');
  
  // Check if phone matches selected network - SOFT WARNING ONLY
  if (normalizedSelected !== normalizedDetected) {
    const prefix = cleanedPhone.substring(0, 3);
    const validPrefixes = NETWORK_PREFIXES[selectedNetwork as keyof NetworkPrefixes] || [];
    
    return {
      isValid: true, // ALLOW PURCHASE - Soft warning only
      network: detectedNetwork,
      error: null,
      warning: `⚠️ This number (${prefix}) appears to be ${detectedNetwork}, but you selected ${selectedNetwork}. Valid ${selectedNetwork} prefixes: ${validPrefixes.join(', ')}. You can continue, but please verify the number is correct.`,
      formattedNumber: cleanedPhone
    };
  }
  
  // Valid match
  return {
    isValid: true,
    network: detectedNetwork,
    error: null,
    warning: null,
    formattedNumber: cleanedPhone
  };
};

/**
 * Get all valid prefixes for a network
 */
export const getNetworkPrefixes = (network: string): string[] => {
  const normalizedNetwork = network.toLowerCase().replace(/\s+/g, '');
  
  switch (normalizedNetwork) {
    case 'mtn':
      return NETWORK_PREFIXES.MTN;
    case 'airteltigo':
      return NETWORK_PREFIXES.AirtelTigo;
    case 'telecel':
    case 'vodafone': // Legacy support
      return NETWORK_PREFIXES.Telecel;
    default:
      return [];
  }
};

/**
 * Get user-friendly network name
 */
export const getNetworkDisplayName = (network: string): string => {
  const normalized = network.toLowerCase().replace(/\s+/g, '');
  
  switch (normalized) {
    case 'mtn':
      return 'MTN';
    case 'airteltigo':
      return 'AirtelTigo';
    case 'telecel':
    case 'vodafone':
      return 'Telecel';
    default:
      return network;
  }
};

/**
 * Format validation error message with helpful info
 */
export const formatValidationError = (
  phone: string,
  selectedNetwork: string
): string => {
  const result = validatePhoneForNetwork(phone, selectedNetwork);
  
  if (result.isValid) {
    return '';
  }
  
  return result.error || 'Invalid phone number';
};

/**
 * Check if phone number can be used with selected network
 */
export const canUsePhoneWithNetwork = (
  phone: string,
  network: string
): boolean => {
  const result = validatePhoneForNetwork(phone, network);
  return result.isValid;
};
